import { useEffect } from 'react'

import { clsx } from 'clsx'
import { Link, useLocation, useOutletContext } from 'react-router'

import { ClampTooltip } from '../../ClampTooltip'
import { getMethodDisplayText } from '~/portal/components/ConsumerSideNavigation/helpers'
import { useScrollIntoView } from '~/portal/hooks/useScrollIntoView'
import { Operation } from '~/portal/types/content'
import { RootOutletContext } from '~/portal/types/portal'
import { buildOperationHash } from '~/portal/utils/api'
import { getOperationCssVar } from '~/portal/utils/shared/operationColor'

export const ApiSubNavOperation = ({
  parentPath,
  operation,
  active = false,
  isSelected,
  setSelectedOperation,
  onClick,
}: {
  parentPath: string,
  operation: Operation,
  active?: boolean,
  isSelected: boolean,
  setSelectedOperation: (selectedOperation: string | null) => void,
  onClick?: (e) => void,
}) => {
  const { method, summary, path, deprecated, operationId } = operation
  const { hash: locationHash } = useLocation()
  const operationHash = buildOperationHash(operation)
  const operationText = summary || path || operationId

  useEffect(() => {
    if (active && operationHash === locationHash) {
      setSelectedOperation(operation.operationId + operation.path + operation.method)
    } else if (!active) {
      setSelectedOperation(null)
    }
  }, [active, operationHash, locationHash, setSelectedOperation, operation.operationId])

  const navActiveItemRef = useScrollIntoView<HTMLAnchorElement>()

  const { closeSideNavWithoutTransition } = useOutletContext<RootOutletContext>()
  
  const onNavigate = (e) => {
    onClick?.(e)
    closeSideNavWithoutTransition?.()
  }

  return (
    <Link to={`${parentPath}${operationHash}`}
      onClick={onNavigate}
      className={clsx(
        'sp-sidenav-api-operation ml-1 grid grid-cols-[2.75rem_1fr] py-1.5 pl-2 pr-8 rounded max-w-full overflow-hidden z-40',
        active && 'hover:bg-(--sp-sidenav-item-hover-bg,var(--sp-color-surface-hover))',
        isSelected ? 'sp-sidenav-api-operation--active bg-(--sp-sidenav-item-active-bg,var(--sp-color-surface-hover)) text-(--sp-color-link) font-semibold' : '',
        deprecated ? 'line-through text-(--sp-sidenav-deprecated-color,var(--neutral-50)) dark:text-(--sp-sidenav-deprecated-color,var(--neutral-50))' : '',
      )} 
      ref={(active && operationHash === locationHash) ? navActiveItemRef : null}
    >
      <div
        className={clsx(
          'sp-operation-method font-semibold uppercase',
          deprecated && 'text-(--sp-sidenav-deprecated-color,var(--neutral-50))! dark:text-(--sp-sidenav-deprecated-color,var(--neutral-50))! line-through'
        )}
        style={{ color: !deprecated ? getOperationCssVar(method) : undefined }}
      >
        {getMethodDisplayText(method)}
      </div>
      <ClampTooltip
        text={operationText}
        className={clsx(isSelected ? 'text-(--sp-color-link) font-semibold' : '')}
      />
    </Link>
  )
}